import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  IconButton,
  LinearProgress,
  MenuItem,
  SvgIcon,
  TextField,
  Typography,
} from "@mui/material";
import * as d3 from "d3";
import { ARAGON_COLORS, useAragonViewport } from "../theme/AragonTheme";
import { SECTOR_OPTIONS } from "../config/businessTaxonomy";

const INTAKE_COLORS = Object.freeze({
  canvas: "#F4F3EF",
  paper: "#FFFEFA",
  line: "rgba(13, 25, 65, 0.14)",
  lineStrong: "rgba(13, 25, 65, 0.26)",
  muted: "#65718A",
  success: "#2E6650",
  successSoft: "#EDF5F0",
  danger: "#8C2338",
  dangerSoft: "#F9E9EC",
  bronze: "#C89B6D",
});


const REPORTING_PERIODS = [
  { value: "FY", label: "Full year (FY)" },
];

const BRIDGE_STATES = [
  {
    id: "VALIDATING_PACKAGE",
    label: "Validating submitted information",
    detail: "Aragon is checking the information package before the valuation run begins.",
  },
  {
    id: "EXTRACTING_FINANCIALS",
    label: "Reading financial statements",
    detail: "CIM is locating and structuring the financial evidence required for analysis.",
  },
  {
    id: "BUILDING_PROFILE",
    label: "Understanding the business",
    detail: "The submitted business context is being prepared for comparable-company analysis.",
  },
  {
    id: "SELECTING_COMPARABLES",
    label: "Identifying relevant comparables",
    detail: "CIM is evaluating market candidates against the target business profile.",
  },
  {
    id: "VALUING",
    label: "Calculating valuation",
    detail: "Comparable-company evidence and target financials are being converted into valuation outputs.",
  },
  {
    id: "COMPLETE",
    label: "Validating valuation output",
    detail: "The analysis is complete. Aragon is checking the returned valuation structure before release.",
  },
];

const MOCK_RESULT = Object.freeze({
  enterprise_value: {
    central_estimate: "2068793910.73468099",
    safe_range: {
      low: "1214048158.496733714",
      high: "2656419287.053401185",
    },
  },
  final: {
    central_estimate: "2959063221.73468099",
    safe_range: {
      low: "2104317469.496733714",
      high: "3546688598.053401185",
    },
  },
});

function InformationIcon(props) {
  return (
    <SvgIcon {...props} viewBox="0 0 24 24">
      <path d="M6.7 3.5h7.7l3.9 3.9v13.1H6.7V3.5Zm1.8 1.8v13.4h8V8.2h-3V5.3h-5Zm2 5h4.3v1.6h-4.3v-1.6Zm0 3.3h4.9v1.6h-4.9v-1.6Z" />
    </SvgIcon>
  );
}

function ValuationIcon(props) {
  return (
    <SvgIcon {...props} viewBox="0 0 24 24">
      <path d="M4 19.5h16v1.7H2.3V3.7H4v15.8Zm3-2.2H5.4v-5.1H7v5.1Zm4.1 0H9.5V7.8h1.6v9.5Zm4.1 0h-1.6V10h1.6v7.3Zm4.1 0h-1.6V5.4h1.6v11.9Z" />
    </SvgIcon>
  );
}

function ResultIcon(props) {
  return (
    <SvgIcon {...props} viewBox="0 0 24 24">
      <path d="M12 2.6a9.4 9.4 0 1 1 0 18.8 9.4 9.4 0 0 1 0-18.8Zm0 1.8a7.6 7.6 0 1 0 0 15.2 7.6 7.6 0 0 0 0-15.2Zm3.8 4.4 1.3 1.2-6 6.3-3.3-3.2 1.2-1.3 2 2 4.8-5Z" />
    </SvgIcon>
  );
}

function UploadIcon(props) {
  return (
    <SvgIcon {...props} viewBox="0 0 24 24">
      <path d="M12 3.2 7.4 7.8l1.2 1.2 2.5-2.5v7.3h1.8V6.5l2.5 2.5 1.2-1.2L12 3.2ZM5 13.5H3.2v5.6c0 1 .8 1.7 1.8 1.7h14c1 0 1.8-.8 1.8-1.7v-5.6H19v5.6H5v-5.6Z" />
    </SvgIcon>
  );
}

function CheckIcon(props) {
  return (
    <SvgIcon {...props} viewBox="0 0 24 24">
      <path d="m9.5 16.4-4-4 1.3-1.3 2.7 2.7 7.7-7.7 1.3 1.3-9 9Z" />
    </SvgIcon>
  );
}

function TrashIcon(props) {
  return (
    <SvgIcon {...props} viewBox="0 0 24 24">
      <path d="M8.3 3.4h7.4l.8 1.8H20v1.7H4V5.2h3.5l.8-1.8Zm-1.5 5h10.4l-.7 11.1H7.5L6.8 8.4Zm2 1.7.5 7.7h5.4l.5-7.7H8.8Zm1.4 1.1h1.5v5.4h-1.5v-5.4Zm2.1 0h1.5v5.4h-1.5v-5.4Z" />
    </SvgIcon>
  );
}

function CancelIcon(props) {
  return (
    <SvgIcon {...props} viewBox="0 0 24 24">
      <path d="M12 2.8a9.2 9.2 0 1 1 0 18.4 9.2 9.2 0 0 1 0-18.4Zm0 1.8a7.4 7.4 0 1 0 0 14.8 7.4 7.4 0 0 0 0-14.8Zm-2.8 3.5L12 10.9l2.8-2.8 1.3 1.3-2.8 2.8 2.8 2.8-1.3 1.3-2.8-2.8-2.8 2.8-1.3-1.3 2.8-2.8-2.8-2.8 1.3-1.3Z" />
    </SvgIcon>
  );
}

function PdfIcon(props) {
  return (
    <SvgIcon {...props} viewBox="0 0 24 24">
      <path d="M6 2.8h8l4 4v14.4H6V2.8Zm1.8 1.8v14.8h8.4V7.6h-3.1v-3H7.8Zm1.1 8h2.2c1.5 0 2.4.8 2.4 2.1 0 1.4-.9 2.2-2.5 2.2h-.7v1.7H8.9v-6Zm1.4 1.3v1.7h.7c.7 0 1-.3 1-.9s-.3-.8-1-.8h-.7Zm4-1.3h1.4v6h-1.4v-6Z" />
    </SvgIcon>
  );
}

function formatBytes(bytes) {
  if (!Number.isFinite(bytes) || bytes <= 0) return "0 KB";
  const units = ["B", "KB", "MB", "GB"];
  const index = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / 1024 ** index;
  return `${value >= 10 || index === 0 ? value.toFixed(0) : value.toFixed(1)} ${units[index]}`;
}

function validateValuationResult(result) {
  const paths = [
    result?.enterprise_value,
    result?.final,
  ];
  return paths.every((entry) => {
    const low = Number(entry?.safe_range?.low);
    const central = Number(entry?.central_estimate);
    const high = Number(entry?.safe_range?.high);
    return [low, central, high].every(Number.isFinite) && low <= central && central <= high;
  });
}

function useMockValuationRun(active) {
  const startedAtRef = useRef(null);
  const [elapsedMs, setElapsedMs] = useState(0);
  useEffect(() => {
    if (!active) {
      startedAtRef.current = null;
      setElapsedMs(0);
      return undefined;
    }
    if (startedAtRef.current === null) startedAtRef.current = Date.now();
    const update = () => setElapsedMs(Date.now() - startedAtRef.current);
    update();
    const timer = window.setInterval(update, 250);
    const onVisibility = () => {
      if (!document.hidden) update();
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [active]);
  const stateDuration = 1500;
  const index = Math.min(Math.floor(elapsedMs / stateDuration), BRIDGE_STATES.length - 1);
  return {
    bridgeState: BRIDGE_STATES[index],
    bridgeIndex: index,
    elapsedMs,
    complete: active && elapsedMs >= stateDuration * BRIDGE_STATES.length,
    result: active && elapsedMs >= stateDuration * BRIDGE_STATES.length ? MOCK_RESULT : null,
  };
}

function PipelineSidebar({ macroState, viewport, metrics }) {
  const [expanded, setExpanded] = useState(false);
  const states = [
    { id: "information", label: "Information", detail: "Business and financial inputs", icon: InformationIcon },
    { id: "valuation", label: "Valuation", detail: "CIM analysis", icon: ValuationIcon },
    { id: "result", label: "Result", detail: "Validated valuation outcome", icon: ResultIcon },
  ];
  const currentIndex = states.findIndex((state) => state.id === macroState);
  const canHover = !viewport.compact;
  return (
    <Box
      component="aside"
      onMouseEnter={() => canHover && setExpanded(true)}
      onMouseLeave={() => setExpanded(false)}
      sx={{
        position: "fixed",
        inset: "0 auto 0 0",
        width: expanded && canHover ? metrics.sidebarExpanded : metrics.sidebarCollapsed,
        bgcolor: ARAGON_COLORS.navy,
        color: "#fff",
        zIndex: 20,
        overflow: "hidden",
        transition: "width 240ms cubic-bezier(0.2, 0.8, 0.2, 1), box-shadow 240ms ease",
        boxShadow: expanded && canHover ? "14px 0 34px rgba(5, 12, 34, 0.18)" : "none",
        borderRight: "1px solid rgba(255,255,255,0.08)",
      }}
      aria-label={`Valuation pipeline status: ${states[currentIndex]?.label || "Information"}`}
    >
      <Box sx={{ height: "100%", px: `${metrics.sidebarPad}px`, py: `${metrics.sidebarPad + 2}px` }}>
        <Box sx={{ display: "flex", alignItems: "center", minHeight: 42, whiteSpace: "nowrap" }}>
          <Box
            component="img"
            src="/images/1080 x 1080 px Logo 1.png"
            alt="Aragon"
            sx={{ width: metrics.logoSize, height: metrics.logoSize, objectFit: "contain", flex: "0 0 auto" }}
          />
          <Box
            sx={{
              ml: 1.4,
              opacity: expanded ? 1 : 0,
              transform: expanded ? "translateX(0)" : "translateX(-6px)",
              transition: "opacity 180ms ease, transform 180ms ease",
              pointerEvents: "none",
            }}
          >
            <Typography sx={{ fontFamily: 'Georgia, "Times New Roman", serif', fontSize: 19, lineHeight: 1 }}>Aragon</Typography>
            <Typography sx={{ mt: 0.55, fontSize: 9, letterSpacing: "0.14em", textTransform: "uppercase", color: "rgba(255,255,255,0.54)" }}>
              Business valuation
            </Typography>
          </Box>
        </Box>
        <Box sx={{ mt: `${metrics.sidebarProcessTop}px`, display: "flex", flexDirection: "column", gap: `${metrics.sidebarStageGap}px` }}>
          {states.map((state, index) => {
            const Icon = state.icon;
            const completed = index < currentIndex;
            const active = index === currentIndex;
            return (
              <Box key={state.id} sx={{ position: "relative", minHeight: metrics.sidebarStageHeight, display: "flex", alignItems: "flex-start", whiteSpace: "nowrap" }}>
                {index < states.length - 1 && (
                  <Box
                    sx={{
                      position: "absolute",
                      left: metrics.iconBox / 2 - 0.5,
                      top: metrics.iconBox + 3,
                      width: 1,
                      height: Math.max(8, metrics.sidebarStageGap + metrics.sidebarStageHeight - metrics.iconBox - 4),
                      bgcolor: index < currentIndex ? INTAKE_COLORS.bronze : "rgba(255,255,255,0.18)",
                    }}
                  />
                )}
                <Box
                  sx={{
                    width: metrics.iconBox,
                    height: metrics.iconBox,
                    borderRadius: "50%",
                    display: "grid",
                    placeItems: "center",
                    border: `1px solid ${active || completed ? INTAKE_COLORS.bronze : "rgba(255,255,255,0.28)"}`,
                    bgcolor: active ? "rgba(200,155,109,0.16)" : "transparent",
                    flex: "0 0 auto",
                  }}
                >
                  {completed ? <CheckIcon sx={{ fontSize: metrics.iconSize, color: INTAKE_COLORS.bronze }} /> : <Icon sx={{ fontSize: metrics.iconSize, color: active ? INTAKE_COLORS.bronze : "rgba(255,255,255,0.56)" }} />}
                </Box>
                <Box
                  sx={{
                    ml: 1.6,
                    pt: 0.15,
                    opacity: expanded ? 1 : 0,
                    transform: expanded ? "translateX(0)" : "translateX(-5px)",
                    transition: "opacity 180ms ease, transform 180ms ease",
                    pointerEvents: "none",
                  }}
                >
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: active ? "#fff" : "rgba(255,255,255,0.74)" }}>{state.label}</Typography>
                  <Typography sx={{ mt: 0.3, fontSize: 10.5, color: "rgba(255,255,255,0.48)" }}>{state.detail}</Typography>
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
}
function StageHeader({ eyebrow, title, description, metrics }) {
  return (
    <Box sx={{ maxWidth: 850 }}>
      <Typography sx={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.13em", textTransform: "uppercase", color: ARAGON_COLORS.burgundy }}>
        {eyebrow}
      </Typography>
      <Typography
        component="h2"
        sx={{
          mt: 0.9,
          fontFamily: 'Georgia, "Times New Roman", serif',
          fontSize: metrics.stageTitle,
          lineHeight: 1.04,
          letterSpacing: "-0.025em",
          color: ARAGON_COLORS.navy,
        }}
      >
        {title}
      </Typography>
      <Typography sx={{ mt: 0.85, maxWidth: 760, fontSize: metrics.bodySize, lineHeight: 1.5, color: INTAKE_COLORS.muted }}>
        {description}
      </Typography>
    </Box>
  );
}

function PdfIntakePanel({ documentState, fiscalYear, reportingPeriod, onSelect, onClear, metrics }) {
  const inputRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);
  const busy = documentState.status === "CHECKING";
  const ready = documentState.status === "READY";
  const invalid = documentState.status === "INVALID";
  const chooseFiles = (fileList) => {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;
    if (files.length > 1) {
      onSelect({ file: files[0], status: "INVALID", progress: 0, error: "Only one PDF can be submitted for a valuation run." });
      return;
    }
    const file = files[0];
    const looksLikePdf = file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf");
    if (!looksLikePdf) {
      onSelect({ file, status: "INVALID", progress: 0, error: "This file was rejected. Aragon accepts PDF financial reports only." });
      return;
    }
    if (file.size <= 0) {
      onSelect({ file, status: "INVALID", progress: 0, error: "This PDF is empty and cannot be submitted." });
      return;
    }
    onSelect({ file, status: "CHECKING", progress: 4, error: "" });
  };
  const openPicker = () => {
    if (busy || ready) return;
    inputRef.current?.click();
  };
  return (
    <Box sx={{ mt: 1.15 }}>
      <input
        ref={inputRef}
        type="file"
        accept="application/pdf,.pdf"
        hidden
        onChange={(event) => {
          chooseFiles(event.target.files);
          event.target.value = "";
        }}
      />
      <Box
          sx={{
            bgcolor: INTAKE_COLORS.paper,
            border: `1px dashed ${
                invalid
                    ? "rgba(140,35,56,0.45)"
                    : dragActive
                        ? ARAGON_COLORS.burgundy
                        : INTAKE_COLORS.lineStrong
            }`,
            borderRadius: 2.2,
            overflow: "hidden",
            transition: "border-color 160ms ease, background-color 160ms ease",
          }}
      >
        <Box
            onDragEnter={(event) => {
              event.preventDefault();
              if (!busy && !ready) setDragActive(true);
            }}
            onDragOver={(event) => event.preventDefault()}
            onDragLeave={(event) => {
              event.preventDefault();
              if (event.currentTarget === event.target) setDragActive(false);
            }}
            onDrop={(event) => {
              event.preventDefault();
              setDragActive(false);
              if (busy || ready) return;
              chooseFiles(event.dataTransfer.files);
            }}
            sx={{
              minHeight: metrics.uploadHeight,
              bgcolor: dragActive
                  ? "rgba(74,3,21,0.035)"
                  : "rgba(13,25,65,0.012)",
              display: "grid",
              placeItems: "center",
              textAlign: "center",
              px: `${metrics.compactCardPad}px`,
            }}
        >
          <Box sx={{ maxWidth: 460 }}>
            <Box sx={{ mx: "auto", width: 42, height: 42, borderRadius: "50%", bgcolor: "rgba(13,25,65,0.055)", display: "grid", placeItems: "center" }}>
              <UploadIcon sx={{ color: ARAGON_COLORS.navy, fontSize: 21 }} />
            </Box>
            <Typography sx={{ mt: 1.1, fontSize: 12.5, fontWeight: 800, color: ARAGON_COLORS.navy }}>
              {ready ? "One financial report is attached" : busy ? "Checking the selected PDF" : "Choose a PDF or drag and drop it here"}
            </Typography>
            <Typography sx={{ mt: 0.35, fontSize: 10.4, lineHeight: 1.45, color: INTAKE_COLORS.muted }}>
              {ready ? "Remove the current file before selecting another." : `PDF only · one file · ${reportingPeriod === "FY" ? `FY${fiscalYear}` : `${reportingPeriod}${fiscalYear}`}` }
            </Typography>
            <Button
              type="button"
              variant="outlined"
              disabled={busy || ready}
              onClick={openPicker}
              sx={{
                mt: 1,
                minWidth: 112,
                minHeight: 32,
                borderColor: INTAKE_COLORS.lineStrong,
                color: ARAGON_COLORS.navy,
                bgcolor: INTAKE_COLORS.paper,
                "&:hover": { borderColor: ARAGON_COLORS.burgundy, bgcolor: "rgba(74,3,21,0.025)" },
              }}
            >
              Browse PDF
            </Button>
          </Box>
        </Box>
        {documentState.file && (
          <Box sx={{ borderTop: `1px solid ${INTAKE_COLORS.line}`, px: `${metrics.uploadRowPadX}px`, py: `${metrics.uploadRowPadY}px` }}>
            <Box sx={{ display: "grid", gridTemplateColumns: "auto minmax(0,1fr) auto", gap: 1.25, alignItems: "center" }}>
              <Box sx={{ width: 38, height: 38, borderRadius: 1.3, bgcolor: invalid ? INTAKE_COLORS.dangerSoft : "rgba(13,25,65,0.055)", display: "grid", placeItems: "center" }}>
                <PdfIcon sx={{ fontSize: 21, color: invalid ? INTAKE_COLORS.danger : ARAGON_COLORS.navy }} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography noWrap sx={{ fontSize: 11.6, fontWeight: 800, color: ARAGON_COLORS.navy }}>{documentState.file.name}</Typography>
                <Typography sx={{ mt: 0.2, fontSize: 9.8, color: INTAKE_COLORS.muted }}>
                  {formatBytes(documentState.file.size || 0)} · {invalid ? "Rejected" : busy ? "Checking PDF" : "Ready for analysis"}
                </Typography>
              </Box>
              {ready ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.55 }}>
                  <Box sx={{ width: 25, height: 25, borderRadius: "50%", bgcolor: INTAKE_COLORS.successSoft, display: "grid", placeItems: "center" }}>
                    <CheckIcon sx={{ fontSize: 15, color: INTAKE_COLORS.success }} />
                  </Box>
                  <IconButton type="button" onClick={onClear} aria-label="Remove uploaded PDF" size="small" sx={{ color: INTAKE_COLORS.muted, "&:hover": { color: INTAKE_COLORS.danger, bgcolor: INTAKE_COLORS.dangerSoft } }}>
                    <TrashIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Box>
              ) : invalid ? (
                <CancelIcon sx={{ fontSize: 23, color: INTAKE_COLORS.danger }} />
              ) : (
                <Typography sx={{ minWidth: 34, textAlign: "right", fontSize: 9.7, fontWeight: 800, color: ARAGON_COLORS.navy }}>{Math.round(documentState.progress || 0)}%</Typography>
              )}
            </Box>
            <LinearProgress
              variant="determinate"
              value={invalid ? 100 : documentState.progress || 0}
              sx={{
                mt: 0.95,
                height: 4,
                borderRadius: 10,
                bgcolor: "rgba(13,25,65,0.07)",
                "& .MuiLinearProgress-bar": {
                  borderRadius: 10,
                  bgcolor: invalid ? INTAKE_COLORS.danger : ready ? INTAKE_COLORS.success : INTAKE_COLORS.bronze,
                  transition: "transform 120ms linear",
                },
              }}
            />
            {invalid && <Typography sx={{ mt: 0.65, fontSize: 10.2, lineHeight: 1.45, color: "#171717" }}>{documentState.error}</Typography>}
          </Box>
        )}
      </Box>
    </Box>
  );
}

function InformationEntry({ form, errors, onChange, fiscalYear, onFiscalYearChange, reportingPeriod, onReportingPeriodChange, documentState, onDocumentSelect, onDocumentClear, onReview, pageError, metrics }) {
  const years = Array.from({ length: 8 }, (_, index) => new Date().getFullYear() - index);
  const fieldSx = {
    alignSelf: "start",
    "& .MuiOutlinedInput-root": { bgcolor: "rgba(255,255,255,0.34)" },
    "& .MuiFormHelperText-root": { mt: 0.45, mx: 0.15, lineHeight: 1.32 },
  };
  return (
    <Box sx={{ maxWidth: metrics.dossierMax }}>
      <Box sx={{ display: "grid", gridTemplateColumns: metrics.intakeHeaderColumns, gap: 2, alignItems: "start" }}>
        <Box>
          <Typography sx={{ fontSize: 9.4, fontWeight: 900, letterSpacing: "0.15em", textTransform: "uppercase", color: INTAKE_COLORS.bronze }}>
            Company valuation intake
          </Typography>
          <Typography
            component="h1"
            sx={{
              mt: 0.65,
              fontFamily: 'Georgia, "Times New Roman", serif',
              fontSize: metrics.intakeTitle,
              lineHeight: 1.03,
              letterSpacing: "-0.025em",
              color: ARAGON_COLORS.navy,
            }}
          >
            New company intake
          </Typography>
          <Typography sx={{ mt: 0.75, maxWidth: 670, fontSize: metrics.bodySize, lineHeight: 1.48, color: INTAKE_COLORS.muted }}>
            Enter the company&apos;s identity and reporting period, then attach its financial report to prepare the valuation run.
          </Typography>
        </Box>
      </Box>
      <Box
        sx={{
          mt: `${metrics.formStartGap}px`,
          display: "flex",
          flexDirection: "column",
          gap: `${metrics.fieldGap}px`,
        }}
      >
        <TextField
          size="small"
          label="Company name"
          value={form.companyName}
          onChange={(event) => onChange("companyName", event.target.value)}
          error={Boolean(errors.companyName)}
          helperText={errors.companyName || "Use the legal or commonly recognised company name."}
          fullWidth
          required
          sx={fieldSx}
        />
        <TextField
          size="small"
          label="Business description"
          value={form.businessDescription}
          onChange={(event) => onChange("businessDescription", event.target.value)}
          error={Boolean(errors.businessDescription)}
          helperText={errors.businessDescription || "Describe what the company sells, who it serves, and how it operates."}
          multiline
          minRows={2}
          fullWidth
          required
          sx={fieldSx}
        />
        <Box sx={{ display: "grid", gridTemplateColumns: metrics.identityColumns, gap: `${metrics.fieldGap}px`, alignItems: "start" }}>
          <TextField
            size="small"
            select
            label="Sector"
            value={form.sector}
            onChange={(event) => onChange("sector", event.target.value)}
            error={Boolean(errors.sector)}
            helperText={errors.sector || "Select the closest primary operating sector."}
            fullWidth
            required
            sx={fieldSx}
          >
            {SECTOR_OPTIONS.map((sector) => <MenuItem key={sector} value={sector}>{sector}</MenuItem>)}
          </TextField>
          <TextField
            size="small"
            label="Sub-sector"
            value={form.subSector}
            onChange={(event) => onChange("subSector", event.target.value)}
            error={Boolean(errors.subSector)}
            helperText={errors.subSector || "Use a specific operating description. “Other” is not accepted."}
            fullWidth
            required
            sx={fieldSx}
          />
        </Box>
        <Box sx={{ display: "grid", gridTemplateColumns: metrics.reportingInputColumns, gap: `${metrics.fieldGap}px`, alignItems: "start" }}>
          <TextField
            size="small"
            label="Employees"
            type="number"
            value={form.employees}
            onChange={(event) => onChange("employees", event.target.value)}
            error={Boolean(errors.employees)}
            helperText={errors.employees || "Optional workforce estimate."}
            inputProps={{ min: 0, step: 1 }}
            fullWidth
            sx={fieldSx}
          />
          <TextField
            size="small"
            select
            label="Fiscal year"
            value={fiscalYear}
            onChange={(event) => onFiscalYearChange(Number(event.target.value))}
            helperText="Select the year reported by the financial document."
            fullWidth
            sx={fieldSx}
          >
            {years.map((year) => <MenuItem key={year} value={year}>{year}</MenuItem>)}
          </TextField>
          <TextField
            size="small"
            select
            label="Reporting period"
            value={reportingPeriod}
            onChange={(event) => onReportingPeriodChange(event.target.value)}
            helperText="Select the reporting period represented by the document."
            fullWidth
            required
            sx={fieldSx}
          >
            {REPORTING_PERIODS.map((period) => <MenuItem key={period.value} value={period.value}>{period.label}</MenuItem>)}
          </TextField>
        </Box>
        <PdfIntakePanel
          documentState={documentState}
          fiscalYear={fiscalYear}
          reportingPeriod={reportingPeriod}
          onSelect={onDocumentSelect}
          onClear={onDocumentClear}
          metrics={metrics}
        />
        {pageError && (
          <Alert severity="error" sx={{ bgcolor: INTAKE_COLORS.dangerSoft, color: "#171717", border: "1px solid rgba(140,35,56,0.16)", "& .MuiAlert-icon": { color: INTAKE_COLORS.danger } }}>
            {pageError}
          </Alert>
        )}
        <Box
          sx={{
            pt: `${metrics.footerTopPad}px`,
            borderTop: `1px solid ${INTAKE_COLORS.line}`,
            display: "grid",
            gridTemplateColumns: metrics.intakeFooterColumns,
            gap: 2,
            alignItems: "center",
          }}
        >
          <Typography sx={{ maxWidth: 430, fontSize: 9.9, lineHeight: 1.45, color: INTAKE_COLORS.muted }}>
            Review the dossier before starting valuation. Once submitted, these inputs are locked for the current run.
          </Typography>
          <Button
            type="button"
            variant="contained"
            onClick={onReview}
            disabled={documentState.status === "CHECKING"}
            sx={{
              justifySelf: metrics.intakeButtonAlign,
              bgcolor: ARAGON_COLORS.burgundy,
              px: 3.1,
              py: 0.95,
              "&:hover": { bgcolor: ARAGON_COLORS.navy },
              "&.Mui-disabled": { bgcolor: "rgba(13,25,65,0.14)", color: "rgba(13,25,65,0.38)" },
            }}
          >
            Review information
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
function ReviewRow({ label, children, onEdit }) {
  return (
    <Box sx={{ py: 1.65, borderBottom: `1px solid ${INTAKE_COLORS.line}`, display: "grid", gridTemplateColumns: "minmax(0,1fr) auto", gap: 2, alignItems: "start" }}>
      <Box>
        <Typography sx={{ fontSize: 9.5, fontWeight: 800, letterSpacing: "0.11em", textTransform: "uppercase", color: ARAGON_COLORS.burgundy }}>{label}</Typography>
        <Box sx={{ mt: 0.65 }}>{children}</Box>
      </Box>
      <Button onClick={onEdit} sx={{ minWidth: 0, px: 0.7, color: ARAGON_COLORS.burgundy }}>Edit</Button>
    </Box>
  );
}

function ReviewStage({ form, fiscalYear, reportingPeriod, documentState, onEdit, onStart, metrics }) {
  const periodLabel = REPORTING_PERIODS.find((period) => period.value === reportingPeriod)?.label || reportingPeriod;
  return (
    <Box sx={{ maxWidth: metrics.dossierMax }}>
      <StageHeader
        eyebrow="Information · Review"
        title="Review the company intake"
        description="Confirm the dossier before committing these inputs to the valuation run."
        metrics={metrics}
      />
      <Box sx={{ mt: `${metrics.formTop}px`, borderTop: `1px solid ${INTAKE_COLORS.line}` }}>
        <ReviewRow label="Identity" onEdit={onEdit}>
          <Typography sx={{ fontFamily: 'Georgia, "Times New Roman", serif', fontSize: 21, color: ARAGON_COLORS.navy }}>{form.companyName}</Typography>
          <Typography sx={{ mt: 0.45, fontSize: 11.5, color: INTAKE_COLORS.muted }}>{form.sector} · {form.subSector}</Typography>
          <Typography sx={{ mt: 0.75, maxWidth: 700, fontSize: 11.2, lineHeight: 1.5, color: INTAKE_COLORS.muted }}>{form.businessDescription}</Typography>
        </ReviewRow>
        <ReviewRow label="Scale & reporting period" onEdit={onEdit}>
          <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: ARAGON_COLORS.navy }}>FY{fiscalYear} · {periodLabel}</Typography>
          {form.employees !== "" && <Typography sx={{ mt: 0.35, fontSize: 11, color: INTAKE_COLORS.muted }}>{form.employees} employees</Typography>}
        </ReviewRow>
        <ReviewRow label="Source document" onEdit={onEdit}>
          <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: ARAGON_COLORS.navy }}>{documentState.file?.name}</Typography>
          <Typography sx={{ mt: 0.35, fontSize: 11, color: INTAKE_COLORS.success }}>Ready · {formatBytes(documentState.file?.size || 0)}</Typography>
        </ReviewRow>
        <Box sx={{ mt: 1.8, p: 1.7, bgcolor: INTAKE_COLORS.successSoft, borderRadius: 1.5, display: "flex", alignItems: "center", gap: 1.25 }}>
          <CheckIcon sx={{ fontSize: 20, color: INTAKE_COLORS.success }} />
          <Box>
            <Typography sx={{ fontSize: 11.8, fontWeight: 800, color: ARAGON_COLORS.navy }}>The intake is ready for valuation.</Typography>
            <Typography sx={{ mt: 0.25, fontSize: 10.5, color: INTAKE_COLORS.muted }}>Starting valuation locks this dossier for the current run.</Typography>
          </Box>
        </Box>
        <Box sx={{ mt: 2.1, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2 }}>
          <Button type="button" onClick={onEdit} sx={{ color: ARAGON_COLORS.navy }}>Back to information</Button>
          <Button variant="contained" onClick={onStart} sx={{ bgcolor: ARAGON_COLORS.burgundy, px: 3.4, py: 1.05, "&:hover": { bgcolor: ARAGON_COLORS.navy } }}>
            Start valuation
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
function ValuationState({ run, metrics }) {
  const total = BRIDGE_STATES.length;
  const stateNumber = Math.min(run.bridgeIndex + 1, total);
  const milestoneProgress = (stateNumber / total) * 100;
  const elapsedSeconds = Math.floor(run.elapsedMs / 1000);
  const minutes = String(Math.floor(elapsedSeconds / 60)).padStart(2, "0");
  const seconds = String(elapsedSeconds % 60).padStart(2, "0");
  return (
    <Box sx={{ maxWidth: 980 }}>
      <StageHeader
        eyebrow="Valuation"
        title="Aragon is analysing the business"
        description="CIM is processing the submitted business and financial information. This page follows the valuation run and updates as its state changes."
        metrics={metrics}
      />
      <Box sx={{ mt: `${metrics.formTop}px`, bgcolor: INTAKE_COLORS.paper, border: `1px solid ${INTAKE_COLORS.line}`, borderRadius: 3, p: `${metrics.valuationPad}px` }}>
        <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 2 }}>
          <Typography sx={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase", color: ARAGON_COLORS.burgundy }}>
            CIM pipeline
          </Typography>
          <Typography sx={{ fontSize: 10.5, color: INTAKE_COLORS.muted }}>Elapsed {minutes}:{seconds}</Typography>
        </Box>
        <Typography sx={{ mt: 2.2, fontFamily: 'Georgia, "Times New Roman", serif', fontSize: metrics.valuationTitle, lineHeight: 1.1, color: ARAGON_COLORS.navy }}>
          {run.bridgeState.label}
        </Typography>
        <Typography sx={{ mt: 1.3, maxWidth: 700, fontSize: 12.5, lineHeight: 1.65, color: INTAKE_COLORS.muted }}>{run.bridgeState.detail}</Typography>
        <LinearProgress
          variant="determinate"
          value={milestoneProgress}
          sx={{
            mt: 3.2,
            height: 6,
            borderRadius: 10,
            bgcolor: "rgba(13,25,65,0.08)",
            "& .MuiLinearProgress-bar": { borderRadius: 10, bgcolor: INTAKE_COLORS.bronze, transition: "transform 500ms ease" },
          }}
        />
        <Box sx={{ mt: 1.1, display: "flex", justifyContent: "space-between", gap: 2 }}>
          <Typography sx={{ fontSize: 10.5, color: INTAKE_COLORS.muted }}>Pipeline milestone {stateNumber} of {total}</Typography>
          <Typography sx={{ fontSize: 10.5, color: INTAKE_COLORS.muted }}>Live valuation state</Typography>
        </Box>
        <Box sx={{ mt: 3.2, display: "grid", gap: 0 }}>
          {BRIDGE_STATES.map((state, index) => {
            const complete = index < run.bridgeIndex;
            const active = index === run.bridgeIndex;
            return (
              <Box key={state.id} sx={{ display: "grid", gridTemplateColumns: "24px minmax(0,1fr)", gap: 1.5, minHeight: 42 }}>
                <Box sx={{ position: "relative", display: "grid", placeItems: "start center" }}>
                  {index < BRIDGE_STATES.length - 1 && <Box sx={{ position: "absolute", top: 15, bottom: -2, width: 1, bgcolor: complete ? INTAKE_COLORS.bronze : INTAKE_COLORS.line }} />}
                  <Box sx={{ mt: 0.25, width: 12, height: 12, borderRadius: "50%", bgcolor: active ? INTAKE_COLORS.bronze : complete ? INTAKE_COLORS.success : INTAKE_COLORS.paper, border: `1px solid ${active ? INTAKE_COLORS.bronze : complete ? INTAKE_COLORS.success : INTAKE_COLORS.lineStrong}`, zIndex: 1 }} />
                </Box>
                <Typography sx={{ pt: 0.05, fontSize: 11.5, fontWeight: active ? 800 : 500, color: active || complete ? ARAGON_COLORS.navy : "rgba(13,25,65,0.42)" }}>{state.label}</Typography>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
}

function ResultState({ valid, metrics }) {
  return (
    <Box sx={{ maxWidth: 900 }}>
      <StageHeader
        eyebrow="Result"
        title={valid ? "Your valuation is ready" : "We couldn't prepare the valuation result"}
        description={valid ? "CIM has completed the analysis and Aragon has verified the required valuation outputs." : "The analysis completed, but the returned valuation output is incomplete or internally inconsistent."}
        metrics={metrics}
      />
      <Box sx={{ mt: `${metrics.formTop}px`, bgcolor: INTAKE_COLORS.paper, border: `1px solid ${valid ? INTAKE_COLORS.line : "rgba(140,35,56,0.24)"}`, borderRadius: 3, p: `${metrics.cardPad}px` }}>
        {valid ? (
          <>
            <Box sx={{ display: "grid", gap: 1.4 }}>
              {["Enterprise value available", "Equity value available", "Valuation ranges verified"].map((label) => (
                <Box key={label} sx={{ display: "flex", alignItems: "center", gap: 1.4 }}>
                  <Box sx={{ width: 28, height: 28, borderRadius: "50%", bgcolor: INTAKE_COLORS.successSoft, display: "grid", placeItems: "center" }}>
                    <CheckIcon sx={{ fontSize: 17, color: INTAKE_COLORS.success }} />
                  </Box>
                  <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: ARAGON_COLORS.navy }}>{label}</Typography>
                </Box>
              ))}
            </Box>
            <Box sx={{ mt: 3.2, pt: 2.5, borderTop: `1px solid ${INTAKE_COLORS.line}`, display: "flex", justifyContent: "flex-end" }}>
              <Button disabled variant="contained" sx={{ bgcolor: ARAGON_COLORS.navy, px: 3.2, "&.Mui-disabled": { bgcolor: "rgba(13,25,65,0.18)", color: "rgba(13,25,65,0.48)" } }}>
                View valuation
              </Button>
            </Box>
            <Typography sx={{ mt: 1.1, textAlign: "right", fontSize: 10, color: INTAKE_COLORS.muted }}>Seller dashboard route will be connected in a later implementation.</Typography>
          </>
        ) : (
          <Alert severity="error" sx={{ bgcolor: INTAKE_COLORS.dangerSoft, color: "#171717", "& .MuiAlert-icon": { color: INTAKE_COLORS.danger } }}>
            Aragon could not validate all required enterprise value and final valuation range fields. Please contact support before relying on this run.
          </Alert>
        )}
      </Box>
    </Box>
  );
}

export default function Intake() {
  const viewport = useAragonViewport();
  const metrics = useMemo(() => {
    const width = viewport.width;
    const height = viewport.height;
    const sidebarCollapsed = Math.round(d3.scaleLinear().domain([320, 1920]).range([54, 72]).clamp(true)(width));
    const sidebarExpanded = Math.round(d3.scaleLinear().domain([900, 1920]).range([220, 270]).clamp(true)(width));
    const mainPadX = Math.round(d3.scaleLinear().domain([320, 1920]).range([18, 64]).clamp(true)(width));
    const mainPadY = Math.round(d3.scaleLinear().domain([600, 1080]).range([20, 34]).clamp(true)(height));
    const contentMax = Math.round(d3.scaleLinear().domain([760, 1920]).range([690, 1180]).clamp(true)(width));
    const stageTitle = Math.round(d3.scaleLinear().domain([320, 1920]).range([31, 46]).clamp(true)(width));
    const bodySize = d3.scaleLinear().domain([320, 1600]).range([12.5, 14]).clamp(true)(width);
    return {
      sidebarCollapsed,
      sidebarExpanded,
      sidebarPad: Math.round(d3.scaleLinear().domain([320, 1920]).range([10, 14]).clamp(true)(width)),
      sidebarProcessTop: Math.round(d3.scaleLinear().domain([600, 1080]).range([12, 18]).clamp(true)(height)),
      sidebarStageGap: Math.round(d3.scaleLinear().domain([600, 1080]).range([8, 11]).clamp(true)(height)),
      sidebarStageHeight: 60,
      logoSize: Math.round(d3.scaleLinear().domain([320, 1920]).range([30, 38]).clamp(true)(width)),
      iconBox: Math.round(d3.scaleLinear().domain([320, 1920]).range([30, 34]).clamp(true)(width)),
      iconSize: Math.round(d3.scaleLinear().domain([320, 1920]).range([16, 18]).clamp(true)(width)),
      mainPadX,
      mainPadY,
      contentMax,
      stageTitle,
      bodySize,
      formTop: Math.round(d3.scaleLinear().domain([600, 1080]).range([13, 18]).clamp(true)(height)),
      fieldGap: Math.round(d3.scaleLinear().domain([320, 1920]).range([12, 18]).clamp(true)(width)),
      formStartGap: Math.round(d3.scaleLinear().domain([600, 1080]).range([18, 24]).clamp(true)(height)),
      footerTopPad: Math.round(d3.scaleLinear().domain([600, 1080]).range([12, 16]).clamp(true)(height)),
      compactCardPad: Math.round(d3.scaleLinear().domain([320, 1920]).range([13, 19]).clamp(true)(width)),
      businessColumns: width >= 1120 ? "minmax(0,0.85fr) minmax(0,1.25fr) minmax(150px,0.6fr)" : width >= 760 ? "1fr 1fr" : "1fr",
      reportingColumns: width >= 1080 ? "minmax(150px,0.8fr) repeat(3,minmax(120px,1fr))" : width >= 700 ? "1fr 1fr" : "1fr",
      reportingGap: Math.round(d3.scaleLinear().domain([320, 1920]).range([12, 20]).clamp(true)(width)),
      dossierMax: Math.round(d3.scaleLinear().domain([760, 1920]).range([690, 980]).clamp(true)(width)),
      intakeTitle: Math.round(d3.scaleLinear().domain([320, 1920]).range([30, 40]).clamp(true)(width)),
      intakeHeaderColumns: width >= 760 ? "minmax(0,1fr) auto" : "1fr",
      intakeMetaAlign: width >= 760 ? "right" : "left",
      identityColumns: width >= 760 ? "1fr 1fr" : "1fr",
      reportingInputColumns: width >= 1060 ? "repeat(3,minmax(0,1fr))" : width >= 700 ? "1fr 1fr" : "1fr",
      intakeFooterColumns: width >= 700 ? "minmax(0,1fr) auto" : "1fr",
      intakeButtonAlign: width >= 700 ? "end" : "stretch",
      cardPad: Math.round(d3.scaleLinear().domain([320, 1920]).range([18, 32]).clamp(true)(width)),
      uploadHeight: Math.round(d3.scaleLinear().domain([600, 1080]).range([500, 220]).clamp(true)(height)),
      uploadInset: Math.round(d3.scaleLinear().domain([320, 1920]).range([10, 14]).clamp(true)(width)),
      uploadRowPadX: Math.round(d3.scaleLinear().domain([320, 1920]).range([14, 20]).clamp(true)(width)),
      uploadRowPadY: Math.round(d3.scaleLinear().domain([600, 1080]).range([11, 15]).clamp(true)(height)),
      documentColumns: width >= 760 ? "minmax(0,1fr) auto" : "1fr",
      valuationPad: Math.round(d3.scaleLinear().domain([320, 1920]).range([20, 36]).clamp(true)(width)),
      valuationTitle: Math.round(d3.scaleLinear().domain([320, 1920]).range([26, 36]).clamp(true)(width)),
    };
  }, [viewport]);
  const [macroState, setMacroState] = useState("information");
  const [informationView, setInformationView] = useState("entry");
  const [informationError, setInformationError] = useState("");
  const [form, setForm] = useState({ companyName: "", businessDescription: "", sector: "", subSector: "", employees: "" });
  const [errors, setErrors] = useState({});
  const [fiscalYear, setFiscalYear] = useState(new Date().getFullYear() - 1);
  const [reportingPeriod, setReportingPeriod] = useState("FY");
  const [documentState, setDocumentState] = useState({ file: null, status: "EMPTY", progress: 0, error: "" });
  const submissionSnapshotRef = useRef(null);
  const [valuationResult, setValuationResult] = useState(null);
  useEffect(() => {
    if (documentState.status !== "CHECKING") return undefined;
    const duration = 900;
    const timer = d3.timer((elapsed) => {
      const progress = Math.min(100, Math.max(4, (elapsed / duration) * 100));
      setDocumentState((current) => {
        if (current.status !== "CHECKING") return current;
        if (progress >= 100) return { ...current, status: "READY", progress: 100, error: "" };
        return { ...current, progress };
      });
      if (progress >= 100) timer.stop();
    });
    return () => timer.stop();
  }, [documentState.status, documentState.file]);
  const run = useMockValuationRun(macroState === "valuation");
  const resultValid = useMemo(() => validateValuationResult(valuationResult), [valuationResult]);
  useEffect(() => {
    if (macroState === "valuation" && run.complete && run.result) {
      setValuationResult(run.result);
      setMacroState("result");
    }
  }, [macroState, run.complete, run.result]);
  const updateForm = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
    setErrors((current) => ({ ...current, [field]: "" }));
    setInformationError("");
  };
  const validateBusiness = () => {
    const nextErrors = {};
    if (!form.companyName.trim()) nextErrors.companyName = "Company name is required.";
    if (form.businessDescription.trim().length < 30) nextErrors.businessDescription = "Provide a more complete description of what the business does.";
    if (!form.sector) nextErrors.sector = "Sector is required.";
    const normalizedSubSector = form.subSector.trim().toLowerCase();
    if (!normalizedSubSector) nextErrors.subSector = "Sub-sector is required.";
    else if (normalizedSubSector === "other" || normalizedSubSector === "others") nextErrors.subSector = "“Other” cannot be used. Enter a specific operating sub-sector.";
    if (form.employees !== "" && (!Number.isInteger(Number(form.employees)) || Number(form.employees) < 0)) nextErrors.employees = "Employees must be a non-negative whole number.";
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };
  const reviewInformation = () => {
    const businessValid = validateBusiness();
    if (documentState.status !== "READY") {
      setInformationError("Attach one valid PDF financial report before reviewing the valuation inputs.");
      return;
    }
    if (!businessValid) {
      setInformationError("Complete the required business information before continuing to review.");
      return;
    }
    setInformationError("");
    setInformationView("review");
  };
  const startValuation = () => {
    if (!validateBusiness() || documentState.status !== "READY") return;
    const snapshot = {
      business: {
        companyName: form.companyName.trim(),
        businessDescription: form.businessDescription.trim(),
        sector: form.sector,
        subSector: form.subSector.trim(),
        employees: form.employees === "" ? 0 : Number(form.employees),
      },
      reportingPeriod: {
        periodType: reportingPeriod,
        fiscalYear,
        periodLabel: reportingPeriod === "FY" ? `FY${fiscalYear}` : `${reportingPeriod}${fiscalYear}`,
      },
      scope: {
        entityScope: "CONSOLIDATED",
        basis: "ACTUAL",
      },
      document: {
        name: documentState.file?.name || "",
        size: documentState.file?.size || 0,
        type: documentState.file?.type || "application/pdf",
      },
    };
    submissionSnapshotRef.current = snapshot;
    setValuationResult(null);
    setMacroState("valuation");
  };
  const updateDocumentState = (nextState) => {
    setDocumentState(nextState);
    setInformationError("");
  };
  const clearDocument = () => {
    setDocumentState({ file: null, status: "EMPTY", progress: 0, error: "" });
    setInformationError("");
  };
  const renderInformation = () => {
    if (informationView === "review") {
      return <ReviewStage form={form} fiscalYear={fiscalYear} reportingPeriod={reportingPeriod} documentState={documentState} onEdit={() => setInformationView("entry")} onStart={startValuation} metrics={metrics} />;
    }
    return (
      <InformationEntry
        form={form}
        errors={errors}
        onChange={updateForm}
        fiscalYear={fiscalYear}
        onFiscalYearChange={(year) => { setFiscalYear(year); setInformationError(""); }}
        reportingPeriod={reportingPeriod}
        onReportingPeriodChange={(period) => { setReportingPeriod(period); setInformationError(""); }}
        documentState={documentState}
        onDocumentSelect={updateDocumentState}
        onDocumentClear={clearDocument}
        onReview={reviewInformation}
        pageError={informationError}
        metrics={metrics}
      />
    );
  };
  return (
    <Box sx={{ height: "100dvh", bgcolor: INTAKE_COLORS.canvas, overflow: "hidden", color: ARAGON_COLORS.navy }}>
      <PipelineSidebar macroState={macroState} viewport={viewport} metrics={metrics} />
      <Box sx={{ ml: `${metrics.sidebarCollapsed}px`, height: "100dvh", minWidth: 0, display: "flex", flexDirection: "column" }}>
        <Box component="main" sx={{ flex: 1, minHeight: 0, overflowY: "auto", px: `${metrics.mainPadX}px`, py: `${metrics.mainPadY}px` }}>
          <Box sx={{ width: "100%", maxWidth: metrics.contentMax, mx: "auto" }}>
            {macroState === "information" && renderInformation()}
            {macroState === "valuation" && <ValuationState run={run} metrics={metrics} />}
            {macroState === "result" && <ResultState valid={resultValid} metrics={metrics} />}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
